
import Vue from "vue";
import { ExactService, IExactDivision } from "@/services/exact-service";
import { YukiService, IYukiAdministration } from "@/services/yuki-service";
import { ClearfactsService, IClearfactsAdministration } from "@/services/clearfacts-service";
import { AccountantService, IAccountantDossier, IntegrationType, IExactDossierConfig, IExactVatCodeOverride, IWinbooksDossierConfig, IWinbooksVatCodeEntry, IYukiDossierConfig, IClearfactsDossierConfig } from "@/services/accountant-service";
import AppExactVatOverridesModal from "@/components/accountant/ExactVatOverridesModal.vue";
import { ISelectItem } from "winfakt-vue-components";

interface IWinbooksVatCodeRow {
	regime: number;
	rateText: string;
	code: string;
	account: string;
}

const WINBOOKS_REGIMES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 16, 17, 19];

function exactSyncDefaults() {
	return {
		exactTab: 0,
		enabled: false,
		autoSyncSales: false,
		autoSyncPurchases: false,
		attachPdf: false,
		vatCodeOverrides: [] as IExactVatCodeOverride[],
		vatModalOpen: false,
		purchaseVatCodeOverrides: [] as IExactVatCodeOverride[],
		purchaseVatModalOpen: false,
		autoProcessSales: false,
		salesExportMode: "invoice" as "invoice" | "entry"
	};
}

function yukiDefaults() {
	return {
		yukiApiKey: "",
		yukiAdministrationId: "",
		yukiDomain: "be" as "be" | "nl",
		yukiProcess: false,
		yukiDefaultGLAccount: "",
		yukiAdministrations: [] as IYukiAdministration[],
		loadingYukiAdministrations: false
	};
}

function clearfactsDefaults() {
	return {
		cfAuthMethod: "oauth" as "oauth" | "token",
		cfApiKey: "",
		cfVatNumber: "",
		cfAdministrationName: "",
		cfSalesFormat: "ubl",
		cfPurchaseFormat: "pdf",
		cfAdministrations: [] as IClearfactsAdministration[],
		loadingCfAdministrations: false
	};
}

function winbooksDefaults() {
	return {
		wbJournalInvoice: "VERKP",
		wbJournalInvoiceType: "2",
		wbJournalCredit: "CNVER",
		wbJournalCreditType: "3",
		wbCentralAccount: "400000",
		wbVatAccount: "451000",
		wbDefaultLedgerAccount: "700000",
		wbLeeggoedAccount: "",
		wbAccountLength: 0,
		wbCustomerNumberField: 0,
		wbQuarterPeriods: false,
		wbBookYearCode: "",
		wbVatCodes: [] as IWinbooksVatCodeRow[]
	};
}

export default Vue.extend({
	data() {
		return {
			integrationType: "manual" as IntegrationType,
			selectedDivision: 0,
			divisionName: "",
			divisionSearch: "",
			divisionMatches: [] as IExactDivision[],
			journal: "",
			defaultVatCode: "",
			defaultItemID: "",
			defaultGLAccountID: "",
			leeggoedGLAccountID: "",
			itemSearch: "",
			glAccountSearch: "",
			leeggoedGLAccountSearch: "",
			leeggoedGLAccounts: [] as {ID:string, Code:string, Description:string, Type:number, BalanceType:string}[],
			purchaseJournal: "",
			defaultPurchaseVatCode: "",
			defaultPurchaseGLAccountID: "",
			purchaseGLAccountSearch: "",
			purchaseGLAccounts: [] as {ID:string, Code:string, Description:string, Type:number, BalanceType:string}[],
			...exactSyncDefaults(),
			...yukiDefaults(),
			...clearfactsDefaults(),
			...winbooksDefaults(),
			divisions: [] as IExactDivision[],
			journals: [] as {Code:string, Description:string, Type:number}[],
			vatCodes: [] as {Code:string, Description:string, Percentage:number}[],
			purchaseVatCodes: [] as {Code:string, Description:string, Percentage:number}[],
			items: [] as {ID:string, Code:string, Description:string}[],
			glAccounts: [] as {ID:string, Code:string, Description:string, Type:number, BalanceType:string}[],
			loadingDivisions: false,
			loadingSubData: false,
			saving: false
		};
	},
	props: {
		dossier: {
			type: Object as () => IAccountantDossier,
			required: true
		},
		exactConnected: Boolean,
		clearfactsConnected: Boolean
	},
	computed: {
		showExactFields():boolean {
			return this.integrationType == "exact" && this.exactConnected && this.selectedDivision > 0;
		},
		showWinbooksFields():boolean {
			return this.integrationType == "winbooks";
		},
		showYukiFields():boolean {
			return this.integrationType == "yuki";
		},
		showClearfactsFields():boolean {
			return this.integrationType == "clearfacts";
		},
		cfAdministrationItems():ISelectItem<string>[] {
			let result:ISelectItem<string>[] = this.cfAdministrations.map(a => ({
				value: a.companyNumber,
				text: a.name ? `${a.name} (${a.companyNumber})` : a.companyNumber
			}));
			if (this.cfVatNumber && !this.cfAdministrations.find(a => a.companyNumber == this.cfVatNumber)) {
				result.unshift({
					value: this.cfVatNumber,
					text: this.cfAdministrationName ? `${this.cfAdministrationName} (${this.cfVatNumber})` : this.cfVatNumber
				});
			}
			return result;
		},
		cfAuthMethodItems():ISelectItem<string>[] {
			return [
				{value: "oauth", text: this.$ct("cf.auth-method-oauth").toString()},
				{value: "token", text: this.$ct("cf.auth-method-token").toString()}
			];
		},
		// Zonder werkende verbinding valt er niets op te halen, dus tonen we de
		// administratiekeuze pas als er een bron van toegang is.
		cfCanChooseAdministration():boolean {
			return this.cfAuthMethod == "token" ? !!this.cfApiKey.trim() : this.clearfactsConnected;
		},
		cfFormatItems():ISelectItem<string>[] {
			return [
				{value: "ubl", text: this.$ct("cf.format-ubl").toString()},
				{value: "pdf", text: this.$ct("cf.format-pdf").toString()},
				{value: "both", text: this.$ct("cf.format-both").toString()}
			];
		},
		yukiDomainItems():ISelectItem<string>[] {
			return [
				{value: "be", text: this.$ct("yuki.domain-be").toString()},
				{value: "nl", text: this.$ct("yuki.domain-nl").toString()}
			];
		},
		yukiAdministrationItems():ISelectItem<string>[] {
			let result:ISelectItem<string>[] = this.yukiAdministrations.map(a => ({
				value: a.ID,
				text: a.Name || a.ID
			}));
			if (this.yukiAdministrationId && !this.yukiAdministrations.find(a => a.ID == this.yukiAdministrationId)) {
				result.unshift({value: this.yukiAdministrationId, text: this.yukiAdministrationId});
			}
			return result;
		},
		wbAccountLengthItems():ISelectItem<number>[] {
			return [
				{value: 0, text: this.$ct("wb.account-length-none").toString()},
				{value: 6, text: "6"},
				{value: 8, text: "8"}
			];
		},
		wbCustomerNumberFieldItems():ISelectItem<number>[] {
			let result:ISelectItem<number>[] = [{value: 0, text: this.$ct("wb.customer-number-contact").toString()}];
			for (let i = 1; i <= 20; i++) {
				result.push({value: i, text: this.$ct("wb.customer-number-extra-field", {number: i}).toString()});
			}
			return result;
		},
		wbRegimeItems():ISelectItem<number>[] {
			return WINBOOKS_REGIMES.map(r => ({
				value: r,
				text: this.$ct(`wb.regime.${r}`).toString()
			}));
		},
		typeItems():ISelectItem<IntegrationType>[] {
			return [
				{value: "manual", text: this.$ct("type.manual").toString()},
				{value: "exact", text: this.$ct("type.exact").toString()},
				{value: "winbooks", text: this.$ct("type.winbooks").toString()},
				{value: "yuki", text: this.$ct("type.yuki").toString()},
				{value: "clearfacts", text: this.$ct("type.clearfacts").toString()}
			];
		},
		salesExportModeItems():ISelectItem<string>[] {
			return [
				{value: "invoice", text: this.$ct("sales-export-mode.invoice").toString()},
				{value: "entry", text: this.$ct("sales-export-mode.entry").toString()}
			];
		},
		journalItems():ISelectItem<string>[] {
			return this.journals.map(j => ({
				value: j.Code,
				text: `${j.Code} — ${j.Description} (type ${j.Type})`
			}));
		},
		purchaseJournalItems():ISelectItem<string>[] {
			let result:ISelectItem<string>[] = [{value: "", text: this.$ct("purchase-journal-same-as-sales").toString()}];
			for (let j of this.journals) {
				result.push({value: j.Code, text: `${j.Code} — ${j.Description} (type ${j.Type})`});
			}
			return result;
		},
		vatCodeItems():ISelectItem<string>[] {
			let result:ISelectItem<string>[] = [{value: "", text: "— (Exact-defaults gebruiken)"}];
			for (let v of this.vatCodes) {
				result.push({value: v.Code, text: `${v.Code} — ${v.Description} (${v.Percentage}%)`});
			}
			return result;
		},
		purchaseVatCodeItems():ISelectItem<string>[] {
			let result:ISelectItem<string>[] = [{value: "", text: "— (Exact-defaults gebruiken)"}];
			for (let v of this.purchaseVatCodes) {
				result.push({value: v.Code, text: `${v.Code} — ${v.Description} (${v.Percentage}%)`});
			}
			return result;
		},
		vatMappingActive():boolean {
			return this.vatCodeOverrides.length > 0;
		},
		purchaseVatMappingActive():boolean {
			return this.purchaseVatCodeOverrides.length > 0;
		},
		salesVatRegimeItems():ISelectItem<number>[] {
			return [1, 2, 3, 9, 6, 11, 17, 19].map(r => ({value: r, text: this.$ct(`vat-overrides.regimes.${r}`).toString()}));
		},
		purchaseVatRegimeItems():ISelectItem<number>[] {
			return [0, 1, 2, 3, 4].map(r => ({value: r, text: this.$ct(`vat-overrides.purchase-regimes.${r}`).toString()}));
		},
		exactTabs():string[] {
			return [
				this.$ct("general-section-title").toString(),
				this.$ct("sales-section-title").toString(),
				this.$ct("purchase-section-title").toString()
			];
		},
		canSave():boolean {
			if (this.saving) return false;
			if (this.integrationType == "manual") return true;
			if (this.integrationType == "exact") {
				let hasItemOrGL = !!this.defaultItemID || !!this.defaultGLAccountID;
				return this.exactConnected && this.selectedDivision > 0 && !!this.journal && hasItemOrGL;
			}
			if (this.integrationType == "winbooks") {
				return !!this.wbJournalInvoice && !!this.wbJournalCredit && !!this.wbCentralAccount && !!this.wbVatAccount;
			}
			if (this.integrationType == "yuki") {
				return !!this.yukiApiKey.trim() && !!this.yukiAdministrationId.trim();
			}
			if (this.integrationType == "clearfacts") {
				return this.cfCanChooseAdministration && !!this.cfVatNumber.trim();
			}
			return false;
		}
	},
	watch: {
		integrationType: {
			immediate: true,
			handler(val:IntegrationType) {
				if (val == "exact" && this.exactConnected && this.divisions.length == 0) {
					this.loadDivisions();
				}
				if (val == "winbooks" && this.wbVatCodes.length == 0) {
					this.prefillWinbooksVatCodes();
				}
			}
		},
		exactConnected: {
			handler(val:boolean) {
				if (val && this.integrationType == "exact" && this.divisions.length == 0 && !this.loadingDivisions) {
					this.loadDivisions();
				}
			}
		},
		selectedDivision: {
			handler(val:number, oldVal:number) {
				if (oldVal && oldVal > 0 && val != oldVal) {
					this.resetDivisionSpecificFields();
				}
				if (val > 0 && this.integrationType == "exact") {
					this.loadSubData();
				}
			}
		}
	},
	methods: {
		errorDetail(err:any):string {
			let data = err?.response?.data;
			if (data && typeof data == "object") {
				if (Array.isArray(data.data) && data.data.length) return String(data.data[0]);
				if (data.message) return String(data.message);
			} else if (typeof data == "string" && data) {
				return data;
			}
			return err?.message || "";
		},
		async loadDivisions() {
			this.loadingDivisions = true;
			try {
				let divisions = await ExactService.getDivisions();
				this.divisions = divisions;
				this.divisionMatches = divisions.slice(0, 50);
				let current = divisions.find(d => d.Code == this.selectedDivision);
				if (current) this.divisionSearch = this.divisionDisplay(current);
				if (divisions.length == 0) {
					this.$wf.notify(this.$ct("error.no-divisions").toString(), "warning", 6000, "exclamation-triangle");
				}
			} catch (err) {
				let detail = this.errorDetail(err);
				let msg = this.$ct("error.fetch-divisions").toString();
				if (detail) msg += `: ${detail}`;
				console.error("[Exact] loadDivisions failed", err);
				this.$wf.notify(msg, "error", 8000, "exclamation-triangle");
			} finally {
				this.loadingDivisions = false;
			}
		},
		divisionDisplay(d:IExactDivision):string {
			return `${d.Description} (${d.Code})`;
		},
		filterDivisions(query:string) {
			let q = (query || "").toLowerCase().trim();
			let list = this.divisions;
			if (q) {
				list = list.filter(d => d.Description.toLowerCase().includes(q) || String(d.Code).includes(q));
			}
			this.divisionMatches = list.slice(0, 50);
		},
		onDivisionFocus() {
			this.filterDivisions("");
		},
		onDivisionTextChange(text:string) {
			this.filterDivisions(text);
		},
		onDivisionPicked(d:IExactDivision) {
			this.divisionSearch = this.divisionDisplay(d);
			this.selectedDivision = d.Code;
		},
		onDivisionBlur() {
			let d = this.divisions.find(x => x.Code == this.selectedDivision);
			if (d) this.divisionSearch = this.divisionDisplay(d);
		},
		async onVatMappingToggle(checked:boolean) {
			if (checked) {
				this.vatModalOpen = true;
				return;
			}
			let ok = await this.$wf.confirm(
				this.$ct("vat-overrides.clear-confirm").toString(),
				this.$ct("vat-overrides.clear-yes").toString(),
				this.$ct("common.cancel").toString(),
				this.$ct("vat-overrides.title").toString(),
				"warning"
			);
			if (ok) {
				this.vatCodeOverrides = [];
			}
		},
		onVatOverridesSaved(overrides:IExactVatCodeOverride[]) {
			this.vatCodeOverrides = overrides;
			this.vatModalOpen = false;
		},
		async onPurchaseVatMappingToggle(checked:boolean) {
			if (checked) {
				this.purchaseVatModalOpen = true;
				return;
			}
			let ok = await this.$wf.confirm(
				this.$ct("vat-overrides.clear-confirm").toString(),
				this.$ct("vat-overrides.clear-yes").toString(),
				this.$ct("common.cancel").toString(),
				this.$ct("vat-overrides.title").toString(),
				"warning"
			);
			if (ok) {
				this.purchaseVatCodeOverrides = [];
			}
		},
		onPurchaseVatOverridesSaved(overrides:IExactVatCodeOverride[]) {
			this.purchaseVatCodeOverrides = overrides;
			this.purchaseVatModalOpen = false;
		},
		resetDivisionSpecificFields() {
			this.journal = "";
			this.defaultVatCode = "";
			this.vatCodeOverrides = [];
			this.purchaseVatCodeOverrides = [];
			this.defaultItemID = "";
			this.itemSearch = "";
			this.defaultGLAccountID = "";
			this.glAccountSearch = "";
			this.leeggoedGLAccountID = "";
			this.leeggoedGLAccountSearch = "";
			this.purchaseJournal = "";
			this.defaultPurchaseVatCode = "";
			this.defaultPurchaseGLAccountID = "";
			this.purchaseGLAccountSearch = "";
			this.journals = [];
			this.vatCodes = [];
			this.purchaseVatCodes = [];
			this.items = [];
			this.glAccounts = [];
			this.leeggoedGLAccounts = [];
			this.purchaseGLAccounts = [];
		},
		async loadSubData() {
			this.loadingSubData = true;
			try {
				let [journals, vatCodes, purchaseVatCodes] = await Promise.all([
					ExactService.getJournals(this.selectedDivision),
					ExactService.getVATCodes(this.selectedDivision),
					ExactService.getPurchaseVATCodes(this.selectedDivision)
				]);
				this.journals = journals;
				this.vatCodes = vatCodes;
				this.purchaseVatCodes = purchaseVatCodes;
			} catch (err) {
				let detail = this.errorDetail(err);
				let msg = this.$ct("error.fetch-sub-data").toString();
				if (detail) msg += `: ${detail}`;
				console.error("[Exact] loadSubData failed", err);
				this.$wf.notify(msg, "error", 8000, "exclamation-triangle");
			} finally {
				this.loadingSubData = false;
			}
		},
		async searchItems(query:string) {
			if (!this.selectedDivision) return;
			if (this.defaultItemID) return;
			try {
				this.items = await ExactService.getItems(this.selectedDivision, query);
			} catch (err) {
				this.items = [];
			}
		},
		onItemTextChange() {
			this.defaultItemID = "";
		},
		onItemPicked(item:{ID:string, Code:string, Description:string}) {
			this.defaultItemID = item.ID;
			this.itemSearch = `${item.Code} — ${item.Description}`;
		},
		async searchGLAccounts(query:string) {
			if (!this.selectedDivision) return;
			if (this.defaultGLAccountID) return;
			try {
				this.glAccounts = await ExactService.getGLAccounts(this.selectedDivision, query);
			} catch (err) {
				this.glAccounts = [];
			}
		},
		onGLAccountTextChange() {
			this.defaultGLAccountID = "";
		},
		onGLAccountPicked(item:{ID:string, Code:string, Description:string}) {
			this.defaultGLAccountID = item.ID;
			this.glAccountSearch = `${item.Code} — ${item.Description}`;
		},
		async searchLeeggoedGL(query:string) {
			if (!this.selectedDivision) return;
			if (this.leeggoedGLAccountID) return;
			try {
				this.leeggoedGLAccounts = await ExactService.getGLAccounts(this.selectedDivision, query);
			} catch (err) {
				this.leeggoedGLAccounts = [];
			}
		},
		onLeeggoedGLTextChange() {
			this.leeggoedGLAccountID = "";
		},
		onLeeggoedGLPicked(item:{ID:string, Code:string, Description:string}) {
			this.leeggoedGLAccountID = item.ID;
			this.leeggoedGLAccountSearch = `${item.Code} — ${item.Description}`;
		},
		async searchPurchaseGL(query:string) {
			if (!this.selectedDivision) return;
			if (this.defaultPurchaseGLAccountID) return;
			try {
				this.purchaseGLAccounts = await ExactService.getGLAccounts(this.selectedDivision, query);
			} catch (err) {
				this.purchaseGLAccounts = [];
			}
		},
		onPurchaseGLTextChange() {
			this.defaultPurchaseGLAccountID = "";
		},
		onPurchaseGLPicked(item:{ID:string, Code:string, Description:string}) {
			this.defaultPurchaseGLAccountID = item.ID;
			this.purchaseGLAccountSearch = `${item.Code} — ${item.Description}`;
		},
		prefillWinbooksVatCodes() {
			this.wbVatCodes = [
				{regime: 1, rateText: "0", code: "211100", account: ""},
				{regime: 1, rateText: "6", code: "211200", account: ""},
				{regime: 1, rateText: "12", code: "211300", account: ""},
				{regime: 1, rateText: "21", code: "211400", account: ""},
				{regime: 2, rateText: "0", code: "212000", account: ""},
				{regime: 3, rateText: "0", code: "221000", account: ""},
				{regime: 6, rateText: "0", code: "211100", account: ""},
				{regime: 4, rateText: "0", code: "", account: ""},
				{regime: 9, rateText: "0", code: "", account: ""},
				{regime: 10, rateText: "0", code: "", account: ""}
			];
		},
		addVatCode() {
			this.wbVatCodes.push({regime: 1, rateText: "21", code: "", account: ""});
		},
		removeVatCode(index:number) {
			this.wbVatCodes.splice(index, 1);
		},
		buildWinbooksConfig():IWinbooksDossierConfig {
			return {
				sales_journal_invoice: this.wbJournalInvoice.trim(),
				sales_journal_invoice_type: this.wbJournalInvoiceType.trim(),
				sales_journal_credit: this.wbJournalCredit.trim(),
				sales_journal_credit_type: this.wbJournalCreditType.trim(),
				central_account: this.wbCentralAccount.trim(),
				vat_account: this.wbVatAccount.trim(),
				default_ledger_account: this.wbDefaultLedgerAccount.trim(),
				leeggoed_account: this.wbLeeggoedAccount.trim(),
				account_length: this.wbAccountLength,
				quarter_periods: this.wbQuarterPeriods,
				book_year_code: this.wbBookYearCode.trim(),
				customer_number_field: this.wbCustomerNumberField,
				vat_codes: this.wbVatCodes
					.filter(e => e.code.trim() != "")
					.map(e => ({
						regime: e.regime,
						rate: parseFloat(e.rateText.replace(",", ".")) || 0,
						code: e.code.trim(),
						ledger_account: e.account.trim()
					}))
			};
		},
		applyWinbooksConfig(cfg:IWinbooksDossierConfig) {
			this.wbJournalInvoice = cfg.sales_journal_invoice || "VERKP";
			this.wbJournalInvoiceType = cfg.sales_journal_invoice_type || "2";
			this.wbJournalCredit = cfg.sales_journal_credit || "CNVER";
			this.wbJournalCreditType = cfg.sales_journal_credit_type || "3";
			this.wbCentralAccount = cfg.central_account || "400000";
			this.wbVatAccount = cfg.vat_account || "451000";
			this.wbDefaultLedgerAccount = cfg.default_ledger_account || "700000";
			this.wbLeeggoedAccount = cfg.leeggoed_account || "";
			this.wbAccountLength = cfg.account_length || 0;
			this.wbQuarterPeriods = cfg.quarter_periods || false;
			this.wbBookYearCode = cfg.book_year_code || "";
			this.wbCustomerNumberField = cfg.customer_number_field || 0;
			this.wbVatCodes = (cfg.vat_codes || []).map((e:IWinbooksVatCodeEntry):IWinbooksVatCodeRow => ({
				regime: e.regime,
				rateText: String(e.rate),
				code: e.code,
				account: e.ledger_account || ""
			}));
			if (this.wbVatCodes.length == 0) {
				this.prefillWinbooksVatCodes();
			}
		},
		async loadYukiAdministrations() {
			if (!this.yukiApiKey.trim()) return;
			this.loadingYukiAdministrations = true;
			try {
				this.yukiAdministrations = await YukiService.getAdministrations(this.yukiApiKey.trim(), this.yukiDomain);
				if (this.yukiAdministrations.length == 0) {
					this.$wf.notify(this.$ct("yuki.no-administrations").toString(), "warning", 6000, "exclamation-triangle");
				} else if (!this.yukiAdministrationId && this.yukiAdministrations.length == 1) {
					this.yukiAdministrationId = this.yukiAdministrations[0].ID;
				}
			} catch (err) {
				let detail = this.errorDetail(err);
				let msg = this.$ct("yuki.fetch-administrations-error").toString();
				if (detail) msg += `: ${detail}`;
				this.$wf.notify(msg, "error", 8000, "exclamation-triangle");
			} finally {
				this.loadingYukiAdministrations = false;
			}
		},
		buildYukiConfig():IYukiDossierConfig {
			return {
				api_key: this.yukiApiKey.trim(),
				administration_id: this.yukiAdministrationId.trim(),
				domain: this.yukiDomain,
				process: this.yukiProcess,
				attach_pdf: this.attachPdf,
				default_gl_account: this.yukiDefaultGLAccount.trim()
			};
		},
		applyYukiConfig(cfg:IYukiDossierConfig) {
			this.yukiApiKey = cfg.api_key || "";
			this.yukiAdministrationId = cfg.administration_id || "";
			this.yukiDomain = cfg.domain == "nl" ? "nl" : "be";
			this.yukiProcess = cfg.process || false;
			this.attachPdf = cfg.attach_pdf || false;
			this.yukiDefaultGLAccount = cfg.default_gl_account || "";
			if (this.yukiApiKey) {
				this.loadYukiAdministrations();
			}
		},
		async loadClearfactsAdministrations() {
			if (!this.cfCanChooseAdministration) return;
			this.loadingCfAdministrations = true;
			try {
				this.cfAdministrations = this.cfAuthMethod == "token"
					? await ClearfactsService.getAdministrationsWithToken(this.cfApiKey.trim())
					: await ClearfactsService.getAdministrations();
				if (this.cfAdministrations.length == 0) {
					this.$wf.notify(this.$ct("cf.no-administrations").toString(), "warning", 6000, "exclamation-triangle");
				} else if (!this.cfVatNumber) {
					// Het btw-nummer van het dossier zelf is bijna altijd de juiste
					// administratie; voorselecteren scheelt de boekhouder een stap.
					let own = this.normalizeVat(this.dossier.VATNumber || "");
					let match = this.cfAdministrations.find(a => this.normalizeVat(a.companyNumber) == own);
					if (match) this.cfVatNumber = match.companyNumber;
				}
			} catch (err) {
				let detail = this.errorDetail(err);
				let msg = this.$ct("cf.fetch-administrations-error").toString();
				if (detail) msg += `: ${detail}`;
				this.$wf.notify(msg, "error", 8000, "exclamation-triangle");
			} finally {
				this.loadingCfAdministrations = false;
			}
		},
		// Clearfacts geeft het ondernemingsnummer zonder landcode of scheidingstekens
		// terug; het btw-nummer van het dossier staat er meestal wel mee.
		normalizeVat(value:string):string {
			return value.replace(/[^0-9]/g, "");
		},
		buildClearfactsConfig():IClearfactsDossierConfig {
			let admin = this.cfAdministrations.find(a => a.companyNumber == this.cfVatNumber);
			return {
				auth_method: this.cfAuthMethod,
				api_key: this.cfAuthMethod == "token" ? this.cfApiKey.trim() : "",
				vat_number: this.cfVatNumber.trim(),
				administration_name: admin?.name || this.cfAdministrationName,
				sales_format: this.cfSalesFormat,
				purchase_format: this.cfPurchaseFormat,
				auto_sync_sales: this.autoSyncSales,
				auto_sync_purchases: this.autoSyncPurchases
			};
		},
		applyClearfactsConfig(cfg:IClearfactsDossierConfig) {
			this.cfAuthMethod = cfg.auth_method == "token" ? "token" : "oauth";
			this.cfApiKey = cfg.api_key || "";
			this.cfVatNumber = cfg.vat_number || "";
			this.cfAdministrationName = cfg.administration_name || "";
			this.cfSalesFormat = cfg.sales_format || "ubl";
			this.cfPurchaseFormat = cfg.purchase_format || "pdf";
			this.autoSyncSales = cfg.auto_sync_sales || false;
			this.autoSyncPurchases = cfg.auto_sync_purchases || false;
			if (this.cfAuthMethod == "token" && this.cfApiKey) {
				this.loadClearfactsAdministrations();
			}
		},
		buildExactConfig():IExactDossierConfig {
			let div = this.divisions.find(d => d.Code == this.selectedDivision);
			return {
				division_id: this.selectedDivision,
				division_name: div?.Description || "",
				journal: this.journal,
				default_vat_code: this.defaultVatCode,
				default_item_id: this.defaultItemID,
				default_gl_account_id: this.defaultGLAccountID,
				leeggoed_gl_account_id: this.leeggoedGLAccountID,
				purchase_journal: this.purchaseJournal,
				default_purchase_vat_code: this.defaultPurchaseVatCode,
				default_purchase_gl_account_id: this.defaultPurchaseGLAccountID,
				auto_sync_sales: this.autoSyncSales,
				auto_sync_purchases: this.autoSyncPurchases,
				auto_process_sales: this.autoProcessSales,
				attach_pdf: this.attachPdf,
				sales_export_mode: this.salesExportMode,
				vat_code_overrides: this.vatCodeOverrides
					.filter(o => !!o.code)
					.map(o => ({regime: Number(o.regime) || 0, rate: Number(o.rate) || 0, code: o.code})),
				purchase_vat_code_overrides: this.purchaseVatCodeOverrides
					.filter(o => !!o.code)
					.map(o => ({regime: Number(o.regime) || 0, rate: Number(o.rate) || 0, code: o.code}))
			};
		},
		async save() {
			this.saving = true;
			let config:object | null = null;
			if (this.integrationType == "winbooks") {
				config = this.buildWinbooksConfig();
			}
			if (this.integrationType == "exact") {
				config = this.buildExactConfig();
			}
			if (this.integrationType == "yuki") {
				config = this.buildYukiConfig();
			}
			if (this.integrationType == "clearfacts") {
				config = this.buildClearfactsConfig();
			}
			try {
				let updated = await AccountantService.setIntegration(this.dossier.DossierID, this.integrationType, config, this.enabled);
				let merged:IAccountantDossier = {
					...this.dossier,
					IntegrationType: updated.IntegrationType as IntegrationType,
					Config: updated.Config,
					Enabled: updated.Enabled
				};
				this.$emit("saved", merged);
			} catch (err) {
				this.$wf.notify(this.$ct("error.save").toString(), "error", 4000, "exclamation-triangle");
			} finally {
				this.saving = false;
			}
		},
		loadFromDossier() {
			this.integrationType = this.dossier.IntegrationType || "manual";
			this.enabled = this.dossier.Enabled;
			if (this.dossier.IntegrationType == "exact" && this.dossier.Config) {
				try {
					this.applyExactConfig(JSON.parse(this.dossier.Config) as IExactDossierConfig);
				} catch {}
			}
			if (this.dossier.IntegrationType == "winbooks" && this.dossier.Config) {
				try {
					this.applyWinbooksConfig(JSON.parse(this.dossier.Config) as IWinbooksDossierConfig);
				} catch {}
			}
			if (this.dossier.IntegrationType == "yuki" && this.dossier.Config) {
				try {
					this.applyYukiConfig(JSON.parse(this.dossier.Config) as IYukiDossierConfig);
				} catch {}
			}
			if (this.dossier.IntegrationType == "clearfacts" && this.dossier.Config) {
				try {
					this.applyClearfactsConfig(JSON.parse(this.dossier.Config) as IClearfactsDossierConfig);
				} catch {}
			}
		},
		applyExactConfig(cfg:IExactDossierConfig) {
			this.selectedDivision = cfg.division_id || 0;
			this.divisionName = cfg.division_name || "";
			if (this.selectedDivision > 0) {
				this.divisionSearch = this.divisionName
					? `${this.divisionName} (${this.selectedDivision})`
					: String(this.selectedDivision);
			}
			this.journal = cfg.journal || "";
			this.defaultVatCode = cfg.default_vat_code || "";
			this.defaultItemID = cfg.default_item_id || "";
			if (this.defaultItemID) {
				this.itemSearch = this.defaultItemID;
			}
			this.defaultGLAccountID = cfg.default_gl_account_id || "";
			if (this.defaultGLAccountID) {
				this.glAccountSearch = this.defaultGLAccountID;
			}
			this.leeggoedGLAccountID = cfg.leeggoed_gl_account_id || "";
			if (this.leeggoedGLAccountID) {
				this.leeggoedGLAccountSearch = this.leeggoedGLAccountID;
			}
			this.purchaseJournal = cfg.purchase_journal || "";
			this.defaultPurchaseVatCode = cfg.default_purchase_vat_code || "";
			this.defaultPurchaseGLAccountID = cfg.default_purchase_gl_account_id || "";
			if (this.defaultPurchaseGLAccountID) {
				this.purchaseGLAccountSearch = this.defaultPurchaseGLAccountID;
			}
			this.autoSyncSales = cfg.auto_sync_sales || false;
			this.autoSyncPurchases = cfg.auto_sync_purchases || false;
			this.autoProcessSales = cfg.auto_process_sales || false;
			this.attachPdf = cfg.attach_pdf || false;
			this.salesExportMode = cfg.sales_export_mode == "entry" ? "entry" : "invoice";
			this.vatCodeOverrides = (cfg.vat_code_overrides || []).map(o => ({...o}));
			this.purchaseVatCodeOverrides = (cfg.purchase_vat_code_overrides || []).map(o => ({...o}));
		}
	},
	mounted() {
		this.loadFromDossier();
	},
	components: {
		AppExactVatOverridesModal
	},
	i18nEx: {
		componentPrefix: "components.accountant-DossierIntegrationForm"
	}
});
